import React from 'react';
import { Link } from 'react-router-dom';
import { Article } from '../../types';
import { formatBlogDate, formatReadingTime, nameInitials } from '../../utils/blogFormat';
import BlogIcon from './BlogIcon';
import ShareControls from './ShareControls';

interface ArticleHeaderProps {
  article: Article;
  url: string;
}

/** بردکرامب + عنوان، لید، متادیتا و دکمه‌های اشتراک (دسکتاپ و موبایل) */
const ArticleHeader: React.FC<ArticleHeaderProps> = ({ article, url }) => {
  const date = formatBlogDate(article.publishedAt || article.createdAt);
  const reading = formatReadingTime(article.readingTime);
  const readingShort = formatReadingTime(article.readingTime, '');
  const author = article.author || 'تحریریه من حقیقی';
  const category = article.category;
  const categoryHref = category ? `/articles?category=${encodeURIComponent(category.slug)}` : '/articles';
  // روتیتر کنار دسته در موبایل (از فیلد «روتیتر» در ویرایشگر)
  const kicker = article.kicker?.trim();

  return (
    <>
      {/* بردکرامب */}
      <nav aria-label="مسیر راهنما" className="pb-3 lg:pb-0">
        <ol className="flex items-center gap-[6px] text-[12px] font-medium leading-5 lg:gap-2">
          <li className="hidden lg:block">
            <Link to="/" className="text-[#5b5f66] hover:text-[#331751]">
              خانه
            </Link>
          </li>
          <li className="hidden text-[10px] text-[#cdc3d0] lg:block" aria-hidden>
            /
          </li>
          <li>
            <Link to="/articles" className="text-[#5b5f66] hover:text-[#331751]">
              مقالات
            </Link>
          </li>
          {category && (
            <>
              <li className="lg:hidden" aria-hidden>
                <BlogIcon name="breadcrumb-chevron" width={4.317} height={7} />
              </li>
              <li className="hidden text-[10px] text-[#cdc3d0] lg:block" aria-hidden>
                /
              </li>
              <li>
                <Link to={categoryHref} className="text-[#1a1c1a]" aria-current="location">
                  {category.name}
                </Link>
              </li>
            </>
          )}
        </ol>
      </nav>

      <header className="mx-auto flex w-full max-w-[960px] flex-col gap-[10px] lg:gap-3 lg:pt-6">
        {/* بج دسته (+ برچسب اول در موبایل) */}
        <div className="flex items-center gap-2">
          {category && (
            <Link
              to={categoryHref}
              className="rounded-full border border-[rgba(205,195,208,0.4)] bg-[#f4f3f1] px-[10px] py-[2px] text-[12px] font-semibold leading-5 text-[#4a2e68] lg:border-[#e9e8e5] lg:bg-[#f4f1ea] lg:px-3 lg:font-medium lg:text-[#5b5f66]"
            >
              {category.name}
            </Link>
          )}
          {kicker && (
            <>
              <span className="text-[12px] font-medium leading-5 text-[#7c757f] lg:hidden" aria-hidden>
                •
              </span>
              <span className="text-[12px] font-medium leading-5 text-[#5b5f66] lg:hidden">{kicker}</span>
            </>
          )}
        </div>

        <h1 className="-mt-[3px] text-[28px] font-bold leading-[41.6px] tracking-[-0.7px] text-[#331751] lg:mt-0 lg:text-[36px] lg:leading-[56px] lg:tracking-normal lg:text-[#1a1c1a]">{article.title}</h1>

        {article.excerpt && (
          <p className="mt-px text-[16px] leading-[26px] text-[#5b5f66] lg:mt-0 lg:max-w-[840px] lg:pb-3 lg:pt-[3px] lg:text-[18px] lg:leading-[29.25px]">{article.excerpt}</p>
        )}

        {/* ردیف متادیتا */}
        <div className="flex flex-col gap-[10px] border-b border-[#efeeeb] pb-3 pt-1 lg:flex-row lg:items-center lg:justify-between lg:border-b-0 lg:border-t lg:border-[#e9e8e5] lg:pb-0 lg:pt-3">
          <div className="flex flex-wrap items-center gap-3 text-[12px] font-medium leading-5 text-[#5b5f66] lg:gap-4">
            <span className="inline-flex items-center gap-1 text-[#1a1c1a] lg:gap-2">
              <span className="hidden h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-[#efdbff] text-[12px] font-bold text-[#331751] lg:inline-flex" aria-hidden>
                {article.authorAvatar ? <img src={article.authorAvatar} alt="" className="h-full w-full object-cover" /> : nameInitials(author)}
              </span>
              <BlogIcon name="author-pen" width={12} height={10.667} className="lg:hidden" />
              {author}
            </span>
            <span className="text-[#cdc3d0] lg:h-1 lg:w-1 lg:rounded-full lg:bg-[#cdc3d0] lg:text-transparent" aria-hidden>
              •
            </span>
            <span className="inline-flex items-center gap-1 lg:gap-[6px]">
              <BlogIcon name="calendar-md" width={12} height={13.333} />
              <time dateTime={article.publishedAt || article.createdAt}>{date}</time>
            </span>
            <span className="text-[#cdc3d0] lg:h-1 lg:w-1 lg:rounded-full lg:bg-[#cdc3d0] lg:text-transparent" aria-hidden>
              •
            </span>
            <span className="inline-flex items-center gap-1 lg:gap-[6px]">
              <BlogIcon name="clock-md" width={13.333} height={13.333} />
              <span className="lg:hidden">{readingShort}</span>
              <span className="hidden lg:inline">{reading}</span>
            </span>
          </div>

          <div className="hidden lg:block">
            <ShareControls url={url} title={article.title} variant="desktop" />
          </div>
        </div>

        <div className="pt-1 lg:hidden">
          <ShareControls url={url} title={article.title} variant="mobile" />
        </div>
      </header>
    </>
  );
};

export default ArticleHeader;
